import { NetworkStatus, gql, useQuery } from '@apollo/client';
import {
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverContent,
  PopoverProps,
  PopoverTrigger,
  Portal,
  Spinner,
  Tag,
  TagLabel,
  TagCloseButton,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import * as React from 'react';
import FocusLock from 'react-focus-lock';

import { PopoverMenuItem, PopoverMenuList } from '@/components/PopoverMenu';
import { SelectButton } from '@/components/SelectButton';
import { XIcon } from '@/imports/ui/chakra/feather';
import { useDebouncedValue } from '@/imports/ui/hooks/useDebouncedValue';

import {
  SearchableTagsSelect_TagsWithSearchTerm3,
  SearchableTagsSelect_TagsWithSearchTerm3Variables,
} from './graphql-types/SearchableTagsSelect_TagsWithSearchTerm3';

const TAGS = gql`
  query SearchableTagsSelect_TagsWithSearchTerm3($input: MultiTagInput) {
    tagsWithSearchTerm(input: $input) {
      tags {
        _id
        name
      }
    }
  }
`;

export const SearchableTagsSelect = React.forwardRef<
  HTMLButtonElement,
  {
    value: { _id: string; name: string }[];
    onChange: any;
    onBlur: () => void;
  } & PopoverProps
>(({ onChange, value, onBlur, ...popoverProps }, ref) => {
  const firstFieldRef = React.useRef(null);
  const [searchText, setSearchText] = React.useState('');
  const debouncedSearchText = useDebouncedValue(searchText, 500);

  const query = useQuery<
    SearchableTagsSelect_TagsWithSearchTerm3,
    SearchableTagsSelect_TagsWithSearchTerm3Variables
  >(TAGS, {
    variables: {
      input: {
        limit: 2000,
        offset: 0,
        filter: {
          searchText: debouncedSearchText,
        },
      },
    },
    notifyOnNetworkStatusChange: true,
  });

  const tags =
    query.networkStatus === NetworkStatus.setVariables
      ? query.previousData?.tagsWithSearchTerm?.tags
      : query.data?.tagsWithSearchTerm?.tags;

  const tagIds = value?.map((tag) => tag._id);

  const updateSelection = (selectedTag: { _id: string; name: string }) => {
    if (tagIds?.includes(selectedTag._id)) {
      return value?.filter((tag) => tag._id !== selectedTag._id);
    } else {
      return value ? [...value, selectedTag] : [selectedTag];
    }
  };

  return (
    <Popover initialFocusRef={firstFieldRef} matchWidth {...popoverProps}>
      <PopoverTrigger>
        <SelectButton ref={ref} w="full" onBlur={onBlur}>
          {value && value.length > 0 ? (
            <HStack>
              {value.map((tag) => {
                return (
                  <Tooltip key={tag._id} label={tag.name} hasArrow>
                    <Tag variant="solid">
                      <TagLabel>{tag.name}</TagLabel>
                      <TagCloseButton
                        onClick={(e) => {
                          e.stopPropagation();
                          const next =
                            value?.filter((t) => t._id !== tag._id) ?? [];
                          onChange(next);
                        }}
                      />
                    </Tag>
                  </Tooltip>
                );
              })}
            </HStack>
          ) : (
            'Select Tags'
          )}
        </SelectButton>
      </PopoverTrigger>
      <Portal>
        <PopoverContent width="full" shadow="lg" maxHeight={56} overflow="auto">
          <FocusLock returnFocus persistentFocus={false}>
            <PopoverMenuList>
              <InputGroup>
                <Input
                  ref={firstFieldRef}
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  placeholder="Search tags"
                  autoComplete="off"
                />
                {query.networkStatus === NetworkStatus.setVariables ? (
                  <InputRightElement>
                    <Spinner color="gray.600" size="sm" />
                  </InputRightElement>
                ) : !!searchText ? (
                  <InputRightElement>
                    <IconButton
                      aria-label="clear search"
                      icon={<XIcon />}
                      onClick={() => setSearchText('')}
                      size="sm"
                      variant="ghost"
                      colorScheme="gray"
                    />
                  </InputRightElement>
                ) : null}
              </InputGroup>
              {tags?.map((tag) => {
                return (
                  <PopoverMenuItem
                    key={tag._id}
                    onClick={() => onChange(updateSelection(tag))}
                    isChecked={value && tagIds?.includes(tag._id)}
                  >
                    {tag.name}
                  </PopoverMenuItem>
                );
              })}

              {tags?.length === 0 && (
                <Text
                  color="gray.500"
                  padding="2"
                  textStyle="compact"
                  textAlign="center"
                >
                  No matching search results
                </Text>
              )}
            </PopoverMenuList>
          </FocusLock>
        </PopoverContent>
      </Portal>
    </Popover>
  );
});

SearchableTagsSelect.displayName = 'SearchableTagsSelect';
